import { createApi } from '@reduxjs/toolkit/query/react';
import customFetchBase from '@/app/common/utils/customFetchBase';
import { getBaseApiPath } from '@/app/common/api/config';
import { handleAiErrorResponse } from '@/data/ai_errors';
import type { ChatMessage } from '@/app/KeywordTracker/types/aiModalTypes';
import type {
  KeywordTrackerAnalysisHistoryDetailsResponse,
  KeywordTrackerAnalysisHistoryDeleteRequest,
  KeywordTrackerAnalysisHistoryDeleteResponse,
  KeywordTrackerAnalysisHistoryItem,
  KeywordTrackerAnalysisHistoryResponse,
  KeywordTrackerAnalysisRequest,
  KeywordTrackerAnalysisResponse
} from '@/app/KeywordTracker/types/aiApiTypes';

function mapHistoryDetailsToChatMessages(
  response: KeywordTrackerAnalysisHistoryDetailsResponse
): ChatMessage[] {
  return (response.data?.messages ?? []).map((msg, index) => ({
    id: new Date(msg.created_at).getTime() + index,
    type: msg.type === 'bot' ? 'ai' : 'user',
    body: { message: msg.message },
    timestamp: msg.created_at
  }));
}

const aiApi = createApi({
  reducerPath: 'ai',
  tagTypes: ['KeywordTrackerAnalysisHistoryList', 'KeywordTrackerAnalysisHistory'],
  baseQuery: (...args: unknown[]) =>
    customFetchBase(getBaseApiPath(), ...args),
  endpoints: build => ({
    getKeywordTrackerAnalysisHistory: build.query<
      KeywordTrackerAnalysisHistoryItem[],
      void
    >({
      query: () => ({
        url: '/ai_binesh/mizfa_tools/v1/keyword-tracker-analysis/history',
        method: 'GET'
      }),
      providesTags: ['KeywordTrackerAnalysisHistoryList'],
      keepUnusedDataFor: 0,
      transformResponse: (response: KeywordTrackerAnalysisHistoryResponse) =>
        response.data ?? [],
      transformErrorResponse: handleAiErrorResponse
    }),
    getKeywordTrackerAnalysisHistoryDetails: build.query<ChatMessage[], string>({
      query: keyword_id => ({
        url: '/ai_binesh/mizfa_tools/v1/keyword-tracker-analysis/history/details',
        method: 'POST',
        body: { keyword_id }
      }),
      providesTags: (_result, _error, keyword_id) => [
        { type: 'KeywordTrackerAnalysisHistory', id: keyword_id }
      ],
      keepUnusedDataFor: 0,
      transformResponse: mapHistoryDetailsToChatMessages,
      transformErrorResponse: handleAiErrorResponse
    }),
    deleteKeywordTrackerAnalysisHistory: build.mutation<
      KeywordTrackerAnalysisHistoryDeleteResponse,
      KeywordTrackerAnalysisHistoryDeleteRequest
    >({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/keyword-tracker-analysis/history/delete',
        method: 'DELETE',
        body
      }),
      async onQueryStarted({ keyword_id }, { dispatch, queryFulfilled }) {
        const patchHistoryList = dispatch(
          aiApi.util.updateQueryData(
            'getKeywordTrackerAnalysisHistory',
            undefined,
            draft => draft.filter(item => item.keyword_id !== keyword_id)
          )
        );
        const patchHistoryDetails = dispatch(
          aiApi.util.updateQueryData(
            'getKeywordTrackerAnalysisHistoryDetails',
            keyword_id,
            () => []
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchHistoryList.undo();
          patchHistoryDetails.undo();
        }
      },
      transformErrorResponse: handleAiErrorResponse
    }),
    keywordTrackerAnalysis: build.mutation<
      KeywordTrackerAnalysisResponse,
      KeywordTrackerAnalysisRequest
    >({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/keyword-tracker-analysis',
        method: 'POST',
        body
      }),
      invalidatesTags: (result, _error, { keyword_id }) =>
        result?.code === 200
          ? [
              'KeywordTrackerAnalysisHistoryList',
              { type: 'KeywordTrackerAnalysisHistory', id: keyword_id }
            ]
          : [],
      transformErrorResponse: handleAiErrorResponse
    })
  })
});

export const {
  useGetKeywordTrackerAnalysisHistoryQuery,
  useGetKeywordTrackerAnalysisHistoryDetailsQuery,
  useDeleteKeywordTrackerAnalysisHistoryMutation,
  useKeywordTrackerAnalysisMutation
} = aiApi;

export default aiApi;
